// src/hooks/useKeyboardShortcuts.ts
// Hook pour gérer les raccourcis clavier globaux
import { useEffect, useCallback, useRef } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  description: string;
  category: string;
  action: () => void;
  enabled?: boolean;
}

export interface ShortcutGroup {
  category: string;
  shortcuts: Omit<KeyboardShortcut, 'action' | 'category'>[];
}

// Formatage de la touche pour affichage
export const formatShortcutKey = (shortcut: Omit<KeyboardShortcut, 'action' | 'category'>): string => {
  const parts: string[] = [];

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  if (shortcut.ctrl) parts.push(isMac ? '⌃' : 'Ctrl');
  if (shortcut.alt) parts.push(isMac ? '⌥' : 'Alt');
  if (shortcut.shift) parts.push(isMac ? '⇧' : 'Shift');
  if (shortcut.meta) parts.push(isMac ? '⌘' : 'Win');

  // Formater la touche
  let key = shortcut.key.toUpperCase();
  if (key === ' ') key = 'Espace';
  if (key === 'ESCAPE') key = 'Échap';
  if (key === 'ARROWUP') key = '↑';
  if (key === 'ARROWDOWN') key = '↓';
  if (key === 'ARROWLEFT') key = '←';
  if (key === 'ARROWRIGHT') key = '→';
  if (key === 'ENTER') key = '↵';

  parts.push(key);

  return parts.join(isMac ? '' : ' + ');
};

// Vérifier si un événement correspond à un raccourci
const matchesShortcut = (event: KeyboardEvent, shortcut: KeyboardShortcut): boolean => {
  const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
  const ctrlMatches = !!shortcut.ctrl === (event.ctrlKey || event.metaKey);
  const shiftMatches = !!shortcut.shift === event.shiftKey;
  const altMatches = !!shortcut.alt === event.altKey;

  return keyMatches && ctrlMatches && shiftMatches && altMatches;
};

// Vérifier si le focus est sur un élément de saisie
const isInputFocused = (): boolean => {
  const activeElement = document.activeElement;
  if (!activeElement) return false;

  const tagName = activeElement.tagName.toLowerCase();
  const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  const isContentEditable = activeElement.getAttribute('contenteditable') === 'true';

  return isInput || isContentEditable;
};

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
  ignoreInputs?: boolean;
}

export const useKeyboardShortcuts = ({
  shortcuts,
  enabled = true,
  ignoreInputs = true,
}: UseKeyboardShortcutsOptions): void => {
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Ignorer si on est dans un champ de saisie
    if (ignoreInputs && isInputFocused()) return;

    for (const shortcut of shortcutsRef.current) {
      if (shortcut.enabled === false) continue;

      if (matchesShortcut(event, shortcut)) {
        event.preventDefault();
        shortcut.action();
        return;
      }
    }
  }, [enabled, ignoreInputs]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

// Hook pour gérer un raccourci unique
export const useKeyboardShortcut = (
  shortcut: Omit<KeyboardShortcut, 'description' | 'category'>,
  enabled = true
): void => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;
    if (isInputFocused()) return;

    const matches =
      event.key.toLowerCase() === shortcut.key.toLowerCase() &&
      !!shortcut.ctrl === (event.ctrlKey || event.metaKey) &&
      !!shortcut.shift === event.shiftKey &&
      !!shortcut.alt === event.altKey;

    if (matches) {
      event.preventDefault();
      shortcut.action();
    }
  }, [shortcut, enabled]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

export default useKeyboardShortcuts;
